/**
 * What the path from the campaign's start to a step says about the leads that reach it
 * (PREC-3897). A campaign is a tree: every step has one incoming edge, and each condition on the
 * way sends a lead down its Yes or its No branch. So the branches a step sits under decide which
 * leads can ever reach it, and some step types cannot work for those leads: a LinkedIn message
 * below "Lead accepted invitation → No" goes to people who are not connected, an email below
 * "Lead has email → No" has no address to go to.
 */

/** The fields of a builder node this module reads. */
export interface PathNode {
	id: string;
	type?: string;
	data?: { conditionalValue?: { value?: string } };
}

/** The fields of a builder edge this module reads. */
export interface PathEdge {
	source: string;
	target: string;
	type?: string;
	sourceHandle?: string | null;
}

type Branch = "yesEdge" | "noEdge";

/** A condition the path passes through, and the branch it takes there. */
export interface PathCondition {
	/** `conditionalValue.value`: "accepted", "hasEmail", "hasLinkedin", "opened", "priority", … */
	value: string;
	branch: Branch;
}

export interface StepPath {
	/** Nearest first: the condition directly above the step comes first. */
	conditions: PathCondition[];
	/** Whether a message, email, invitation or InMail runs before the step. */
	hasPrecedingSend: boolean;
}

/** A step type that cannot reach the leads on its path, and why. */
export type StepPathBlock = "NO_LINKEDIN" | "NOT_ACCEPTED" | "NO_EMAIL";

export type StepPathWarning = StepPathBlock | "FIRST_MESSAGE_NEEDS_CONNECTION";

/** Every warning, so a spec can prove each one has its words. */
export const STEP_PATH_WARNINGS: readonly StepPathWarning[] = [
	"NO_LINKEDIN",
	"NOT_ACCEPTED",
	"NO_EMAIL",
	"FIRST_MESSAGE_NEEDS_CONNECTION",
];

/** The translation key of each warning — a closed map, never the code spliced into a key. */
export const STEP_PATH_WARNING_KEYS: Record<StepPathWarning, string> = {
	NO_LINKEDIN: "list.campaignBuilder.stepPath.noLinkedin",
	NOT_ACCEPTED: "list.campaignBuilder.stepPath.notAccepted",
	NO_EMAIL: "list.campaignBuilder.stepPath.noEmail",
	FIRST_MESSAGE_NEEDS_CONNECTION: "list.campaignBuilder.stepPath.firstMessageNeedsConnection",
};

const SEND_STEP_TYPES = new Set([
	"emailNode",
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
]);

const LINKEDIN_STEP_TYPES = new Set([
	"linkedinNode",
	"linkedinInviteNode",
	"linkedinInmailNode",
	"linkedinVisitNode",
]);

const isBranch = (handle: string | null | undefined): handle is Branch =>
	handle === "yesEdge" || handle === "noEdge";

/**
 * Collects what lies above `startId`, walking the incoming edges back to the start. With
 * `includeStart`, `startId` itself counts as running before — it is the step a new one is added
 * below; without it, a step does not describe itself.
 */
const walkUp = (
	startId: string,
	nodes: PathNode[],
	edges: PathEdge[],
	includeStart: boolean
): StepPath => {
	const byId = new Map(nodes.map((node) => [node.id, node]));
	const incoming = new Map(edges.map((edge) => [edge.target, edge]));
	const conditions: PathCondition[] = [];
	const visited = new Set<string>();
	let hasPrecedingSend = false;
	let childId = startId;

	if (includeStart && SEND_STEP_TYPES.has(byId.get(startId)?.type ?? "")) {
		hasPrecedingSend = true;
	}

	// A path is never longer than the graph, so a loop in bad data cannot hang the builder.
	while (!visited.has(childId) && visited.size <= nodes.length) {
		visited.add(childId);

		const edge = incoming.get(childId);
		const parent = edge ? byId.get(edge.source) : undefined;

		if (!edge || !parent) {
			break;
		}

		const handle = edge.sourceHandle ?? edge.type;
		const value = parent.data?.conditionalValue?.value;

		if (parent.type === "conditionalNode" && value && isBranch(handle)) {
			conditions.push({ value, branch: handle });
		}

		if (SEND_STEP_TYPES.has(parent.type ?? "")) {
			hasPrecedingSend = true;
		}

		childId = parent.id;
	}

	return { conditions, hasPrecedingSend };
};

/** The path from the start down to an existing step. */
export const describeStepPath = (nodeId: string, nodes: PathNode[], edges: PathEdge[]): StepPath =>
	walkUp(nodeId, nodes, edges, false);

/**
 * The path a new step will sit on when it is added below `initiatorId` — through the initiator's
 * `edgeType` branch when the initiator is a condition.
 */
export const describeInsertionPath = (
	initiatorId: string,
	edgeType: string | undefined,
	nodes: PathNode[],
	edges: PathEdge[]
): StepPath => {
	const above = walkUp(initiatorId, nodes, edges, true);
	const initiator = nodes.find((node) => node.id === initiatorId);
	const value = initiator?.data?.conditionalValue?.value;

	if (initiator?.type !== "conditionalNode" || !value || !isBranch(edgeType)) {
		return above;
	}

	return { ...above, conditions: [{ value, branch: edgeType }, ...above.conditions] };
};

/**
 * The branch the path last took at a condition of this kind — a later check overrides an earlier
 * one ("not accepted", then a second invitation, then "accepted" is a path of accepted leads).
 */
const nearestBranch = (path: StepPath, value: string): Branch | null =>
	path.conditions.find((condition) => condition.value === value)?.branch ?? null;

/** Why a step of `stepType` cannot reach the leads on `path`, or null when it can. */
export const blockForStep = (stepType: string, path: StepPath): StepPathBlock | null => {
	if (LINKEDIN_STEP_TYPES.has(stepType) && nearestBranch(path, "hasLinkedin") === "noEdge") {
		return "NO_LINKEDIN";
	}

	if (stepType === "linkedinNode" && nearestBranch(path, "accepted") === "noEdge") {
		return "NOT_ACCEPTED";
	}

	if (stepType === "emailNode" && nearestBranch(path, "hasEmail") === "noEdge") {
		return "NO_EMAIL";
	}

	return null;
};

/**
 * A LinkedIn message that opens the campaign reaches only leads who are already connections —
 * every other lead fails at it — unless a "connected" check has already sent it only the leads
 * who are.
 */
export const isFirstMessageWithoutConnection = (stepType: string, path: StepPath): boolean =>
	stepType === "linkedinNode" &&
	!path.hasPrecedingSend &&
	nearestBranch(path, "accepted") !== "yesEdge";

/** The one warning a step shows: a path that blocks it outranks the first-message caveat. */
export const resolveStepPathWarning = (stepType: string, path: StepPath): StepPathWarning | null =>
	blockForStep(stepType, path) ??
	(isFirstMessageWithoutConnection(stepType, path) ? "FIRST_MESSAGE_NEEDS_CONNECTION" : null);
